import { Box, Button, CircularProgress, Typography } from '@mui/material';
import type { FC } from 'react';

import ErrorSection from 'components/ErrorSection/ErrorSection';
import Popup from 'components/Popup/Popup';
import SuccessSection from 'components/SuccessSection/SuccessSection';
import { useResponsive } from 'contexts/ResponsiveContext';
import { ModalSizeE } from 'declarations/enums/modal';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import type { NoopT } from 'declarations/types/common';

import useTicketClosePopup from './hooks/useTicketClosePopup';
import { getPopupContentStyle } from './TicketClosePopup.styled';

type TicketClosePopupPropsT = {
  ticketId: string;
  isOpen: boolean;
  onClose: NoopT;
};

const TicketClosePopup: FC<TicketClosePopupPropsT> = ({ ticketId, isOpen, onClose }) => {
  const { isMobile } = useResponsive();
  const {
    isClosingTicket,
    isTicketSuccessfullyClosed,
    isTicketClosingError,
    showPopupContent,
    handleCloseTicket,
    handleClosePopup,
    closeTicketErrorMessage,
    closeTicketValidationErrorMessage,
  } = useTicketClosePopup();

  return (
    <Popup
      nested
      isOpen={isOpen}
      showCloseBtn
      modalTitle="Close Ticket"
      size={ModalSizeE.CLOSE_TICKET}
      popupId={ScrollLockIdE.TicketClosePopup}
      contentStyle={getPopupContentStyle(isMobile)}
      onClose={handleClosePopup(onClose)}
      closeOnEscape={false}
    >
      {showPopupContent && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100%',
          }}
        >
          <Typography variant="body1" color="text.primary" align={isMobile ? 'center' : 'left'}>
            Are you sure you want to close the ticket?
          </Typography>
          <Box sx={{ flex: 1, minHeight: 16 }} />
          <Box
            display="flex"
            justifyContent={isMobile ? 'center' : 'flex-end'}
            sx={{ mt: 2, pt: 1 }}
          >
            <Button
              variant="contained"
              disabled={isClosingTicket}
              onClick={handleCloseTicket([ticketId])}
              startIcon={
                isClosingTicket ? <CircularProgress size={16} color="inherit" /> : undefined
              }
            >
              Close
            </Button>
          </Box>
        </Box>
      )}

      {isTicketSuccessfullyClosed && (
        <Box sx={{ p: { xs: 0, md: '0 2.5rem 0.625rem' } }}>
          <SuccessSection message="Ticket successfully closed" />
        </Box>
      )}

      {isTicketClosingError && (
        <Box sx={{ p: { xs: 0, md: '0 2.5rem 0.625rem' } }}>
          <ErrorSection
            errorDetails={closeTicketErrorMessage || closeTicketValidationErrorMessage}
          />
        </Box>
      )}
    </Popup>
  );
};

export default TicketClosePopup;
